import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TimeZoneOption } from "@/lib/utils/timeZone";

type TimeZoneFieldWithModalProps = {
  value: string;
  options: TimeZoneOption[];
  onChange: (nextTimeZone: string) => void;
  className?: string;
};

export const TimeZoneFieldWithModal = ({
  value,
  options,
  onChange,
  className,
}: TimeZoneFieldWithModalProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? value;

  useEffect(() => {
    if (!isOpen) {
      setDraftValue(value);
    }
  }, [isOpen, value]);

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <Label>{t("common.timeZone")}</Label>
        <button
          type="button"
          className="text-sm text-rw-main underline"
          onClick={() => setIsOpen(true)}
        >
          {t("common.change")}
        </button>
      </div>
      <p className="mt-1 text-sm text-rw-black-gray">{selectedLabel}</p>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common.selectTimeZone")}</DialogTitle>
          </DialogHeader>

          <Select value={draftValue} onValueChange={setDraftValue}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => {
                onChange(draftValue);
                setIsOpen(false);
              }}
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
